import { useCallback, useEffect, useState } from 'react';

import { CAMERA_RESOLUTION } from '@/lib/cameraCapture';

export interface MediaDevicesState {
  audioInputs: MediaDeviceInfo[];
  videoInputs: MediaDeviceInfo[];
  audioOutputs: MediaDeviceInfo[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useMediaDevices(): MediaDevicesState {
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const enumerate = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setError(new Error('enumerateDevices non supporté'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      try {
        // Contraint : une webcam ne sert qu'un format à la fois, et cet amorçage tourne aussi sur
        // 'devicechange', donc en plein live. Ouvert sans contrainte, il impose son 640x480 à la piste publiée.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {
            width: CAMERA_RESOLUTION.width,
            height: CAMERA_RESOLUTION.height,
            frameRate: CAMERA_RESOLUTION.frameRate,
          },
        });
        stream.getTracks().forEach((t) => t.stop());
      } catch {}

      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioInputs(devices.filter((d) => d.kind === 'audioinput'));
      setVideoInputs(devices.filter((d) => d.kind === 'videoinput'));
      setAudioOutputs(devices.filter((d) => d.kind === 'audiooutput'));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    enumerate();
  }, [enumerate]);

  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return;
    const handleChange = () => {
      enumerate();
    };
    navigator.mediaDevices.addEventListener('devicechange', handleChange);
    return () => navigator.mediaDevices.removeEventListener('devicechange', handleChange);
  }, [enumerate]);

  return { audioInputs, videoInputs, audioOutputs, loading, error, refresh: enumerate };
}
